import { useEffect, useRef, useState } from "react";
import "@/App.css";
import axios from "axios";
import Lenis from "@studio-freight/lenis";
import { Menu, X, Check } from "lucide-react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Assets
const HERO_LOGO_URL = "https://customer-assets.emergentagent.com/job_premium-merch-2/artifacts/ubsscvng_LOGO%20NUEVO%20URBAN%20NOIRE%202026.png";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_premium-merch-2/artifacts/59mk71zo_ChatGPT%20Image%2015%20mar%202026%2C%2021_58_46.png";
const CONTACT_LOGO_URL = "https://customer-assets.emergentagent.com/job_premium-merch-2/artifacts/cz8h3wjx_LOGO%20NUEVO%20URBAN%20NOIRE%202026.png";

const GALLERY_IMAGES = [
  { url: "https://customer-assets.emergentagent.com/job_premium-merch-2/artifacts/zzh0gfl5_1%20TOYOTA%20UNP.png", alt: "Toyota TRD Pro branded cap" },
  { url: "https://customer-assets.emergentagent.com/job_premium-merch-2/artifacts/a4h6e12v_2%20CONTAINER%20UNP.png", alt: "Cartagena Experiences branded bottle" },
  { url: "https://customer-assets.emergentagent.com/job_premium-merch-2/artifacts/zi1ixct0_3%20N.png", alt: "ORVYN Barber House premium gift set" },
  { url: "https://customer-assets.emergentagent.com/job_premium-merch-2/artifacts/9guk7cy3_4%20N.png", alt: "US Motorsports Racing Team polo" },
  { url: "https://customer-assets.emergentagent.com/job_premium-merch-2/artifacts/81sessgp_5%20BMW%20UNP.png", alt: "BMW Motorsport jacket" },
  { url: "https://customer-assets.emergentagent.com/job_premium-merch-2/artifacts/iszxca02_6%20TERPEL%20UNP.png", alt: "Terpel branded uniform shirt" },
];

// Intersection Observer Hook
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.1, ...options });

    if (node) {
      observer.observe(node);
    }

    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [ref, isInView];
};

// Header Component - Minimal, intentional
const NAV_ITEMS = ["Work", "Philosophy", "Contact"];

const NavLinks = ({ onSelect, mobile = false }) => (
  <nav className={mobile ? "flex flex-col py-8" : "hidden md:flex items-center gap-16"}>
    {NAV_ITEMS.map((item) => (
      <button
        key={item}
        onClick={() => onSelect(item.toLowerCase())}
        data-testid={mobile ? undefined : `nav-${item.toLowerCase()}`}
        className={
          mobile
            ? "px-8 py-5 text-left text-[11px] uppercase tracking-[0.3em] text-white/60 hover:text-white transition-all duration-500"
            : "text-[11px] uppercase tracking-[0.3em] text-white/60 hover:text-white transition-colors duration-500 font-light"
        }
      >
        {item}
      </button>
    ))}
  </nav>
);

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        isScrolled ? "header-blur bg-black/90" : "bg-transparent"
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-8 md:px-16 lg:px-24">
        <div className="flex items-center justify-end h-24 md:h-28">
          <NavLinks onSelect={scrollToSection} />
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="w-5 h-5 text-white" />
            ) : (
              <Menu className="w-5 h-5 text-white" />
            )}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden absolute top-full left-0 right-0 bg-black/98 header-blur border-t border-white/5"
        >
          <NavLinks onSelect={scrollToSection} mobile />
        </div>
      )}
    </header>
  );
};

// Hero Section - Cinematic, Psychological, Luxury
const HeroBackground = () => (
  <>
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{
        backgroundImage: `url(${HERO_IMAGE})`,
        filter: "blur(2px)",
        transform: "scale(1.02)",
      }}
    />
    <div className="absolute inset-0 bg-black/60" />
  </>
);

const HeroContent = ({ isInView, onCTAClick }) => (
  <>
    {/* Logo */}
    <div className={`${isInView ? "animate-fade-in-up" : "opacity-0"} mb-20 md:mb-28`}>
      <img
        src={HERO_LOGO_URL}
        alt="Urban Noire"
        data-testid="hero-logo"
        className="w-72 sm:w-80 md:w-[26rem] lg:w-[32rem] xl:w-[38rem] h-auto mx-auto"
      />
    </div>

    {/* Tagline - Psychological, Status-driven */}
    <div className={`${isInView ? "animate-fade-in-up delay-200" : "opacity-0"} mb-10 md:mb-14`}>
      <p
        data-testid="hero-headline"
        className="text-[11px] sm:text-xs md:text-sm uppercase tracking-[0.4em] text-white/50 font-light"
      >
        Perception Creates Value
      </p>
    </div>

    {/* Sub-message */}
    <div className={`${isInView ? "animate-fade-in-up delay-300" : "opacity-0"} mb-16 md:mb-20`}>
      <p
        data-testid="hero-subheadline"
        className="max-w-md mx-auto text-sm md:text-base text-white/40 font-light tracking-wide leading-relaxed"
      >
        A luxury creative house for brands that refuse to look average.
      </p>
    </div>

    {/* CTA - Premium, Exclusive */}
    <div className={`${isInView ? "animate-fade-in-up delay-500" : "opacity-0"}`}>
      <button
        data-testid="hero-cta"
        onClick={onCTAClick}
        className="group relative px-8 py-4 text-[10px] md:text-[11px] uppercase tracking-[0.35em] text-white/80 border border-white/20 hover:border-white/40 hover:text-white transition-all duration-700"
      >
        Request A Brand Consultation
        <span className="absolute bottom-0 left-0 w-0 h-px bg-white/40 group-hover:w-full transition-all duration-700" />
      </button>
    </div>
  </>
);

const HeroSection = () => {
  const [ref, isInView] = useInView();

  const scrollToContact = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <HeroBackground />
      <div
        ref={ref}
        className="relative z-10 w-full max-w-screen-2xl mx-auto px-8 md:px-16 lg:px-24 py-32 flex flex-col items-center justify-center min-h-screen text-center"
      >
        <HeroContent isInView={isInView} onCTAClick={scrollToContact} />
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-10">
        <div className="w-px h-16 bg-gradient-to-b from-transparent via-white/20 to-transparent" />
      </div>
    </section>
  );
};

// Manifesto Section - Bold, Psychological
const ManifestoSection = () => {
  const [ref, isInView] = useInView();

  return (
    <section
      id="about"
      data-testid="manifesto-section"
      className="py-40 md:py-56 lg:py-64"
    >
      <div
        ref={ref}
        className="max-w-screen-xl mx-auto px-8 md:px-16 lg:px-24"
      >
        <div className={`max-w-4xl ${isInView ? "animate-fade-in-up" : "opacity-0"}`}>
          <p className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light leading-[1.4] tracking-tight text-white/90">
            We don't make merchandise.
            <br />
            <span className="text-white/40">We build perception.</span>
          </p>
        </div>
        
        <div className={`mt-20 md:mt-28 max-w-2xl ${isInView ? "animate-fade-in-up delay-300" : "opacity-0"}`}>
          <p className="text-sm md:text-base text-white/40 font-light leading-relaxed tracking-wide">
            Every brand tells a story before a word is spoken. The question is whether that story commands attention or gets ignored. Urban Noire exists for businesses that understand this difference.
          </p>
        </div>
      </div>
    </section>
  );
};

// Work Section - Editorial Gallery
const WorkSection = () => {
  const [ref, isInView] = useInView();

  return (
    <section
      id="work"
      data-testid="work-section"
      className="py-32 md:py-40 lg:py-48 bg-[#050505]"
    >
      <div
        ref={ref}
        className="max-w-screen-2xl mx-auto px-8 md:px-16 lg:px-24"
      >
        <div className={`mb-20 md:mb-28 ${isInView ? "animate-fade-in-up" : "opacity-0"}`}>
          <p className="text-[11px] uppercase tracking-[0.4em] text-white/30 mb-6">Selected Work</p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-light tracking-tight text-white/90">
            Brands We've Elevated
          </h2>
        </div>
        
        {/* Editorial Gallery Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {GALLERY_IMAGES.map((image, index) => (
            <div
              key={image.url}
              data-testid={`gallery-item-${index}`}
              className={`gallery-item-luxury ${
                isInView
                  ? `animate-scale-in delay-${(index + 1) * 100}`
                  : "opacity-0"
              }`}
            >
              <img 
                src={image.url} 
                alt={image.alt} 
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Philosophy Section - Confident, Minimal
const PRINCIPLES = [
  {
    number: "01",
    title: "Perception Is Currency",
    text: "The way your brand looks determines how much people are willing to pay. We design for premium positioning."
  },
  {
    number: "02",
    title: "Details Signal Status",
    text: "Quality is noticed subconsciously. Every stitch, every texture, every finish tells your clients who you are."
  },
  {
    number: "03",
    title: "Presence Over Promotion",
    text: "The best marketing doesn't feel like marketing. It feels like belonging to something exclusive."
  }
];

const PhilosophySection = () => {
  const [ref, isInView] = useInView();

  return (
    <section
      id="philosophy"
      data-testid="philosophy-section"
      className="py-40 md:py-56 lg:py-64"
    >
      <div
        ref={ref}
        className="max-w-screen-xl mx-auto px-8 md:px-16 lg:px-24"
      >
        <div className={`mb-24 md:mb-32 ${isInView ? "animate-fade-in-up" : "opacity-0"}`}>
          <p className="text-[11px] uppercase tracking-[0.4em] text-white/30 mb-6">Our Philosophy</p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-light tracking-tight text-white/90 max-w-2xl">
            We believe branding is psychology, not decoration.
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-16 md:gap-12 lg:gap-20">
          {PRINCIPLES.map((principle, index) => (
            <div
              key={principle.number}
              className={`${isInView ? `animate-fade-in-up delay-${(index + 2) * 100}` : "opacity-0"}`}
            >
              <p className="text-[10px] tracking-[0.3em] text-white/20 mb-6">{principle.number}</p>
              <h3 className="text-lg md:text-xl font-light tracking-tight text-white/80 mb-4">
                {principle.title}
              </h3>
              <p className="text-sm text-white/35 font-light leading-relaxed">
                {principle.text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Statement Section - Bold, Cinematic
const StatementSection = () => {
  const [ref, isInView] = useInView();

  return (
    <section
      data-testid="statement-section"
      className="py-40 md:py-56 lg:py-64 bg-[#050505]"
    >
      <div
        ref={ref}
        className="max-w-screen-xl mx-auto px-8 md:px-16 lg:px-24 text-center"
      >
        <div className={`${isInView ? "animate-fade-in-up" : "opacity-0"}`}>
          <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-light leading-[1.4] tracking-tight text-white/80 max-w-4xl mx-auto">
            "If your brand doesn't look expensive,
            <br />
            <span className="text-white/40 italic">it won't feel expensive."</span>
          </p>
        </div>
      </div>
    </section>
  );
};

// Contact Section - Exclusive, Premium
const INITIAL_FORM = {
  full_name: "",
  company_name: "",
  position: "",
  email: "",
  phone: "",
};

const VALIDATORS = {
  full_name: (v) => {
    if (!v || v.trim() === "") return "Required";
    if (v.trim().length < 2) return "Too short";
    return "";
  },
  company_name: (v) => (!v || v.trim() === "" ? "Required" : ""),
  position: (v) => (!v || v.trim() === "" ? "Required" : ""),
  email: (v) => {
    if (!v || v.trim() === "") return "Required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return "Invalid email";
    return "";
  },
  phone: (v) => {
    if (!v) return "Required";
    if (v.replace(/\D/g, "").length < 7) return "Invalid number";
    return "";
  },
};

const validateField = (name, value) =>
  VALIDATORS[name] ? VALIDATORS[name](value) : "";

const TEXT_FIELDS = [
  { name: "full_name", placeholder: "Your Name", testId: "input-full-name" },
  { name: "company_name", placeholder: "Company / Brand", testId: "input-company-name" },
  { name: "position", placeholder: "Your Role", testId: "input-position" },
  { name: "email", type: "email", placeholder: "Email", testId: "input-email" },
];

const submitLead = (formData) => axios.post(`${API}/leads`, formData);

const useContactForm = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const validateAllFields = () => {
    const newErrors = {};
    Object.keys(formData).forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) newErrors[field] = error;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) {
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
    setSubmitError("");
  };

  const handlePhoneChange = (value) => {
    setFormData((prev) => ({ ...prev, phone: value || "" }));
    if (touched.phone) {
      setErrors((prev) => ({ ...prev, phone: validateField("phone", value) }));
    }
    setSubmitError("");
  };

  const handleBlur = (name) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, formData[name]) }));
  };

  const resetForm = () => {
    setIsSubmitted(true);
    setFormData(INITIAL_FORM);
    setErrors({});
    setTouched({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const allTouched = Object.keys(formData).reduce((acc, k) => ({ ...acc, [k]: true }), {});
    setTouched(allTouched);
    if (!validateAllFields()) return;

    setIsSubmitting(true);
    setSubmitError("");
    try {
      const response = await submitLead(formData);
      if (response.data.status === "success") resetForm();
    } catch (err) {
      setSubmitError("Something went wrong. Please try again.");
      console.error("Form submission error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    errors,
    touched,
    isSubmitting,
    isSubmitted,
    submitError,
    handleChange,
    handlePhoneChange,
    handleBlur,
    handleSubmit,
  };
};

const TextField = ({ name, type = "text", placeholder, value, error, touched, onChange, onBlur, testId }) => (
  <div className="relative">
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      onBlur={() => onBlur(name)}
      placeholder={placeholder}
      data-testid={testId}
      className={`luxury-input ${error && touched ? "border-white/30" : ""}`}
    />
    {error && touched && (
      <span className="absolute right-0 top-1/2 -translate-y-1/2 text-[10px] tracking-wider text-white/30">{error}</span>
    )}
  </div>
);

const SuccessBanner = () => (
  <div
    data-testid="form-success"
    className="flex items-center gap-4 mb-10 px-6 py-5 border border-white/15 bg-white/[0.02]"
  >
    <div className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center flex-shrink-0">
      <Check className="w-4 h-4 text-white/70" />
    </div>
    <div>
      <p className="text-sm font-light text-white/80 mb-1">Request Received</p>
      <p className="text-[11px] tracking-wider text-white/40 font-light leading-relaxed">
        Your request has been received successfully. Our team will contact you shortly.
      </p>
    </div>
  </div>
);

const ContactInfo = ({ isInView }) => (
  <div className={`${isInView ? "animate-fade-in-up" : "opacity-0"}`}>
    <p className="text-[11px] uppercase tracking-[0.4em] text-white/30 mb-8">Begin</p>
    <h2
      data-testid="contact-headline"
      className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white/90 mb-8 leading-[1.2]"
    >
      Let's build something
      <br />
      <span className="text-white/50 italic">that looks expensive.</span>
    </h2>
    <p className="text-sm text-white/35 font-light leading-relaxed mb-12 max-w-md">
      Share a few details about your business. If we're the right fit, we'll reach out to discuss your brand transformation.
    </p>
    <div className="hidden lg:block">
      <img
        src={CONTACT_LOGO_URL}
        alt="Urban Noire"
        className="w-full max-w-[70%] h-auto opacity-80"
      />
    </div>
  </div>
);

const PhoneField = ({ value, error, touched, onChange, onBlur }) => (
  <div className="relative">
    <PhoneInput
      international
      defaultCountry="US"
      value={value}
      onChange={onChange}
      onBlur={() => onBlur("phone")}
      placeholder="Phone"
      data-testid="input-phone"
      className={`luxury-phone-input ${error && touched ? "phone-input-error" : ""}`}
    />
    {error && touched && (
      <span className="absolute right-0 top-1/2 -translate-y-1/2 text-[10px] tracking-wider text-white/30">{error}</span>
    )}
  </div>
);

const ContactForm = ({ form }) => {
  const {
    formData, errors, touched, isSubmitting, isSubmitted, submitError,
    handleChange, handlePhoneChange, handleBlur, handleSubmit,
  } = form;

  return (
    <>
      {isSubmitted && <SuccessBanner />}
      <form
        data-testid="contact-form"
        onSubmit={handleSubmit}
        className="space-y-8"
        noValidate
      >
        {TEXT_FIELDS.map((f) => (
          <TextField
            key={f.name}
            name={f.name}
            type={f.type}
            placeholder={f.placeholder}
            testId={f.testId}
            value={formData[f.name]}
            error={errors[f.name]}
            touched={touched[f.name]}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        ))}

        <PhoneField
          value={formData.phone}
          error={errors.phone}
          touched={touched.phone}
          onChange={handlePhoneChange}
          onBlur={handleBlur}
        />

        {submitError && (
          <p data-testid="form-error" className="text-[11px] tracking-wider text-white/40">
            {submitError}
          </p>
        )}

        <button
          type="submit"
          data-testid="submit-button"
          disabled={isSubmitting}
          className="w-full py-5 mt-4 text-[10px] md:text-[11px] uppercase tracking-[0.35em] text-white/70 border border-white/15 hover:border-white/30 hover:text-white transition-all duration-700 disabled:opacity-30"
        >
          {isSubmitting ? "Submitting..." : "Submit Request"}
        </button>
      </form>
    </>
  );
};

const ContactSection = () => {
  const [ref, isInView] = useInView();
  const form = useContactForm();

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-40 md:py-56 lg:py-64"
    >
      <div
        ref={ref}
        className="max-w-screen-xl mx-auto px-8 md:px-16 lg:px-24"
      >
        <div className="grid lg:grid-cols-2 gap-20 lg:gap-32 items-start">
          <ContactInfo isInView={isInView} />
          <div className={`${isInView ? "animate-fade-in-up delay-200" : "opacity-0"}`}>
            <ContactForm form={form} />
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer - Minimal
const Footer = () => {
  return (
    <footer
      data-testid="footer"
      className="py-16 border-t border-white/5"
    >
      <div className="max-w-screen-2xl mx-auto px-8 md:px-16 lg:px-24">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          <img
            src={HERO_LOGO_URL}
            alt="Urban Noire"
            className="h-8 w-auto opacity-50"
          />
          <p className="text-[10px] text-white/25 font-light tracking-[0.2em] uppercase">
            © {new Date().getFullYear()} Urban Noire USA
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App
function App() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.4,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: "vertical",
      gestureDirection: "vertical",
      smooth: true,
      smoothTouch: false,
      touchMultiplier: 2,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="App bg-black min-h-screen" data-testid="app-container">
      <div className="noise-overlay" />
      <Header />
      <main>
        <HeroSection />
        <ManifestoSection />
        <WorkSection />
        <PhilosophySection />
        <StatementSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
